import Icon from '@components/dataDisplay/Icon'
import { BLACK, GREY_COLORS, RED_COLORS } from '@config/theme/colors'
import { ChevronRightRounded } from '@mui/icons-material'
import { Stack, styled, Typography } from '@mui/material'
import { useState } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import TraineeCompanyPopIn from '../../modules/dashboard/components/trainees/TraineeCompanyPopIn'

interface CompanySelectInputProps<T extends FieldValues> extends UseControllerProps<T> {
	isRequired?: boolean
	label?: string
	companyLabel: string
	setCompanyLabel: (value: string) => void
	isDisabled: boolean
}

/**
 * Company select input
 * @param control
 * @param name
 * @param label
 * @param companyLabel
 * @param setCompanyLabel
 * @param isDisabled
 * @param rules
 * @param isRequired
 * @constructor
 */
const CompanySelectInput = <T extends FieldValues>(
	{
		control,
		name,
		label,
		companyLabel,
		setCompanyLabel,
		isDisabled,
		rules,
		isRequired
	}: CompanySelectInputProps<T>
) => {
	const [isTraineeCompanyPopInOpen, setIsTraineeCompanyPopInOpen] = useState(false)

	const { field, fieldState: { error } } = useController({
		control,
		name,
		rules: {
			...rules,
			required: isRequired
		}
	})

	return (
		<Container>
			{label && (
				<Typography>
					{label}
				</Typography>
			)}
			<Input
				className={!isDisabled ? 'enabled' : 'disabled'}
				sx={{ border: `1px solid ${!!error ? RED_COLORS[100] : GREY_COLORS[400]}` }}
				onClick={() => !isDisabled && setIsTraineeCompanyPopInOpen(true)}
			>
				<Typography color={companyLabel ? BLACK : GREY_COLORS[200]}>
					{companyLabel || <FormattedMessage id="dashboard.trainees.actions.addTrainee.fields.company.placeholder" />}
				</Typography>
				<Icon IconElement={ChevronRightRounded} />
			</Input>
			{!!error && (
				<Typography color={RED_COLORS[200]} variant="body2">
					<FormattedMessage id={`global.errors.${error.type}`} />
				</Typography>
			)}
			{isTraineeCompanyPopInOpen && (
				<TraineeCompanyPopIn
					onClose={() => setIsTraineeCompanyPopInOpen(false)}
					onConfirm={(option) => {
						field.onChange(option.value)
						setCompanyLabel(option.label)
						setIsTraineeCompanyPopInOpen(false)
					}}
					selectedCompany={field.value as string}
					isOpen
				/>
			)}
		</Container>
	)
}

const Container = styled(Stack)({
	rowGap: '8px'
})

const Input = styled(Stack)({
	alignItems: 'center',
	borderRadius: '8px',
	cursor: 'pointer',
	flexDirection: 'row',
	justifyContent: 'space-between',
	padding: '8px 12px',
	'&.enabled:hover': {
		backgroundColor: GREY_COLORS[50]
	},
	'&.disabled': {
		backgroundColor: GREY_COLORS[100],
		color: GREY_COLORS[600],
		cursor: 'not-allowed'
	}
})

export default CompanySelectInput
